import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

export type PublicSeoDefinition = {
    title: MessageDescriptor;
    description: MessageDescriptor;
    image?: string;
    keywords?: MessageDescriptor;
    robots?: string;
    structuredData?: 'agents' | 'home' | 'research-briefs';
};

/**
 * Translator-facing metadata for manifest-backed public pages.
 *
 * Paths and exposure policy deliberately live in the canonical public-web
 * manifest. Keeping copy here lets Lingui extract it without duplicating URL
 * ownership in page components.
 */
export const PUBLIC_SEO_DEFINITIONS = {
    home: {
        title: msg({
            id: 'seo.home.title',
            message: 'Relevant, continuously updated research data feeds | Paperzilla',
            comment: 'Public home-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.home.description',
            message: 'Paperzilla ranks new papers from arXiv, bioRxiv, PubMed, and your alerts against your topic. Get them by email, in the app, or as a morning brief in Claude or ChatGPT.',
            comment: 'Public home-page metadata description. Preserve product, source, and protocol names.',
        }),
        structuredData: 'home',
    },
    product: {
        title: msg({
            id: 'seo.product.title',
            message: 'Paperzilla Product | Research data feeds',
            comment: 'Public product-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.product.description',
            message: 'Paperzilla turns scattered papers, datasets, software repositories, patents, and alerts into focused research data feeds for researchers, teams, and AI agents.',
            comment: 'Public product-page metadata description.',
        }),
    },
    features: {
        title: msg({
            id: 'seo.features.title',
            message: 'Paperzilla Features & Use Cases',
            comment: 'Public features-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.features.description',
            message: 'Explore Paperzilla features for always-on research data monitoring, relevance ranking, email and RSS delivery, AI summaries, MCP, CLI, API, and AI agent workflows.',
            comment: 'Public features-page metadata description. Preserve protocol names.',
        }),
    },
    about: {
        title: msg({
            id: 'seo.about.title',
            message: 'About Paperzilla',
            comment: 'Public about-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.about.description',
            message: 'Paperzilla helps researchers, R&D teams, and AI agents stay current by turning scattered papers, datasets, repositories, patents, and alerts into focused research data feeds.',
            comment: 'Public about-page metadata description.',
        }),
    },
    agents: {
        title: msg({
            id: 'seo.agents.title',
            message: 'AI Agents welcome | Paperzilla',
            comment: 'Public AI-agents page browser, Open Graph, Twitter, and JSON-LD title.',
        }),
        description: msg({
            id: 'seo.agents.description',
            message: 'Connect Claude, Codex, OpenClaw, and other AI agents to Paperzilla projects through MCP, the pz CLI, or RSS/Atom. Monitor papers and prepare research briefs.',
            comment: 'Public AI-agents page metadata description. Preserve product and protocol names.',
        }),
        keywords: msg({
            id: 'seo.agents.keywords',
            message: 'AI research agents, Paperzilla MCP, Paperzilla CLI, OpenClaw, Claude, Codex, research automation, research briefs',
            comment: 'Comma-separated public AI-agents page metadata keywords. Preserve product names and commas.',
        }),
        robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
        structuredData: 'agents',
    },
    'research-briefs': {
        title: msg({ id: 'seo.researchBriefs.title', message: 'Morning research briefs with your AI assistant | Paperzilla' }),
        description: msg({ id: 'seo.researchBriefs.description', message: 'Connect your Paperzilla research feeds to Claude, ChatGPT, or any AI assistant that supports MCP. Every morning, get the new papers worth reading, why they matter, and answers to your follow-up questions.' }),
        keywords: msg({ id: 'seo.researchBriefs.keywords', message: 'morning research brief, daily research digest, AI research assistant, Claude research, ChatGPT research, Paperzilla MCP' }),
        robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
        structuredData: 'research-briefs',
    },
    forward: {
        title: msg({
            id: 'seo.forward.title',
            message: 'Paperzilla Forward',
            comment: 'Public Forward-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.forward.description',
            message: 'Forward Google Scholar, Semantic Scholar, HuggingFace, journal, and other paper-alert emails into Paperzilla, then turn them into relevance-filtered research feeds.',
            comment: 'Public Forward-page metadata description. Preserve source and product names.',
        }),
        image: '/images/paperzilla-forward.jpg',
    },
    'public-digests': {
        title: msg({
            id: 'seo.publicDigests.title',
            message: 'Public Research Digests - Paperzilla',
            comment: 'Public-digests browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.publicDigests.description',
            message: 'Browse all public research digests created by Paperzilla users. Discover how researchers track papers in their fields.',
            comment: 'Public-digests metadata description.',
        }),
    },
    'example-summaries': {
        title: msg({
            id: 'seo.exampleSummaries.title',
            message: 'Example Paper Summaries - Paperzilla',
            comment: 'Example-summaries browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.exampleSummaries.description',
            message: 'Browse papers with AI-generated summaries from public research digests.',
            comment: 'Example-summaries metadata description.',
        }),
    },
    summarize: {
        title: msg({
            id: 'seo.summarize.title',
            message: 'Paper Archive - Paperzilla',
            comment: 'Public summarize-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.summarize.description',
            message: 'Browse existing paper analyses, summaries, quality ratings, and limitations in the Paperzilla archive. New PDF uploads are no longer available.',
            comment: 'Public summarize-page metadata description.',
        }),
    },
    trial: {
        title: msg({
            id: 'seo.trial.title',
            message: 'Start your Starter trial | Paperzilla',
            comment: 'Starter-trial browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.trial.description',
            message: 'Resume tracking new research with a Paperzilla Starter trial.',
            comment: 'Starter-trial metadata description.',
        }),
    },
    'trial-preview': {
        title: msg({
            id: 'seo.trialPreview.title',
            message: 'Starter trial | Paperzilla',
            comment: 'Starter-trial preview browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.trialPreview.description',
            message: 'Securely verify your Paperzilla Starter trial link.',
            comment: 'Starter-trial preview metadata description.',
        }),
    },
    upgrade: {
        title: msg({
            id: 'seo.upgrade.title',
            message: 'Paperzilla Pro',
            comment: 'Upgrade-page browser, Open Graph, and Twitter title.',
        }),
        description: msg({
            id: 'seo.upgrade.description',
            message: 'Upgrade to Paperzilla Pro and get deeper insights with fact-checked citations, author background research, and advanced AI analysis.',
            comment: 'Upgrade-page metadata description.',
        }),
    },
} as const satisfies Record<string, PublicSeoDefinition>;

export type PublicSeoSurfaceId = keyof typeof PUBLIC_SEO_DEFINITIONS;

export const STRUCTURED_DATA_COPY = {
    homeSoftwareDescription: msg({
        id: 'seo.home.softwareApplication.description',
        message: 'Relevant, continuously updated research paper feeds from paper sources and forwarded alerts for researchers, teams, and AI agents.',
        comment: 'SoftwareApplication JSON-LD description on the public home page.',
    }),
    agentsAboutAiAgents: msg({
        id: 'seo.agents.about.aiAgents',
        message: 'AI agents',
        comment: 'Thing name in AI-agents page JSON-LD.',
    }),
    agentsAboutMonitoring: msg({
        id: 'seo.agents.about.researchMonitoring',
        message: 'Research monitoring',
        comment: 'Thing name in AI-agents page JSON-LD.',
    }),
    agentsAboutMcp: msg({
        id: 'seo.agents.about.modelContextProtocol',
        message: 'Model Context Protocol',
        comment: 'Thing name in AI-agents page JSON-LD. Preserve the protocol name.',
    }),
} as const;
